import React, { useState, useEffect } from "react";
import { Breadcrumb, Row, Col, Spin, Alert, Button, message, Modal } from "antd";
import { HomeOutlined, UserOutlined, EyeOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";

import CustomerHeader from "./components/CustomerHeader";
import CustomerDetailsCard from "./components/CustomerDetailsCard";
import CollectionTable from "./components/CollectionTable";
import AssetListTable from "./components/AssetListTable";
// import VoiceHistoryTable from "./components/VoiceHistoryTable";
import ActionPanel from "./components/ActionPanel";
import StatisticsCard from "./components/StatisticsCard";
import CustomerFormDrawer from "../CustomerFormDrawer";
import { containerStyle, spinContainerStyle, alertContainerStyle } from "./styles";
import { customerAPIWithErrorHandling } from "../../../../api/customer/customer";
import { collectionAPIWithErrorHandling } from "../../../../api/collection/collection";
// import { voiceIpAPIWithErrorHandling } from "../../../../api/voiceIP/voiceIP";
// import { useSip } from "../../../../contexts/SipContext";

export default function InfoCustomer() {
  const { id } = useParams();
  const navigate = useNavigate();
  // const { callNow, ready, ua } = useSip();
  // const { callNow } = useSip();

  // States
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  // const [loadingVoice, setLoadingVoice] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [dataCollection, setDataCollection] = useState([]);
  // const [dataVoices, setDataVoices] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  // const [paginationVoice, setPaginationVoice] = useState({ current: 1, pageSize: 10, total: 0 });

  // Fetch customer data
  const fetchCustomerInfo = async (id) => {
    if (!id) {
      message.error("ID khách hàng không hợp lệ");
      return;
    }

    setLoading(true);
    try {
      const response = await customerAPIWithErrorHandling.getCustomerById(id);
      const customerData = response.data?.data || response.data;
      setCustomer(customerData);
    } catch (error) {
      message.error(error.message || "Không thể tải thông tin khách hàng");
      console.error("Error fetching customer:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load collection data
  const loadDataCollection = async (id, params = {}) => {
    try {
      setLoading(true);
      const response = await collectionAPIWithErrorHandling.getCollectionForCustomers(id, {
        page: pagination.current,
        per_page: pagination.pageSize,
        ...params,
      });

      if (response.data.success) {
        const { data: responseData } = response.data;
        setDataCollection(responseData.data);
        setPagination({
          ...pagination,
          current: responseData.current_page,
          total: responseData.total,
        });
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    fetchCustomerInfo(id);
    loadDataCollection(id);
  }, [id]);

  // Handlers
  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    loadDataCollection(id, {
      page: newPagination.current,
      per_page: newPagination.pageSize,
    });
  };

  const handleBack = () => navigate("/customers");

  // Sửa ngay tại màn chi tiết bằng drawer, không rời trang rồi phải quay lại
  const handleEdit = () => setEditOpen(true);

  const handleToggleStatus = async () => {
    if (!customer) return;
    const newStatus = !customer.status;
    const statusText = newStatus ? "kích hoạt" : "vô hiệu hóa";

    Modal.confirm({
      title: `Xác nhận ${statusText} tài khoản`,
      content: `Bạn có chắc chắn muốn ${statusText} tài khoản của "${customer.name}"?`,
      onOk: async () => {
        setUpdating(true);
        try {
          await customerAPIWithErrorHandling.updateCustomerStatus(customer.id, newStatus);
          setCustomer({ ...customer, status: newStatus });
          message.success(`Đã ${statusText} tài khoản thành công`);
        } catch (error) {
          message.error(error.message || `Không thể ${statusText} tài khoản`);
        } finally {
          setUpdating(false);
        }
      },
    });
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Xác nhận xóa khách hàng",
      content: `Bạn có chắc chắn muốn xóa khách hàng "${customer.name}"? Tài sản đứng tên khách hàng sẽ được xóa theo.`,
      okType: "danger",
      onOk: async () => {
        try {
          await customerAPIWithErrorHandling.deleteCustomer(customer.id);
          message.success("Xóa khách hàng thành công");
          handleBack();
        } catch (error) {
          // Lý do bị chặn kèm cả tên đợt thu nên dài, message dòng đơn sẽ cắt cụt
          Modal.error({
            title: "Không thể xóa khách hàng",
            content: error.message || "Đã có lỗi xảy ra, vui lòng thử lại.",
          });
        }
      },
    });
  };

  // Loading state
  if (loading) {
    return (
      <div style={spinContainerStyle}>
        <Spin size="large" tip="Đang tải thông tin khách hàng..." />
      </div>
    );
  }

  // Error state
  if (!customer) {
    return (
      <div style={alertContainerStyle}>
        <Alert
          message="Không tìm thấy thông tin khách hàng"
          type="error"
          showIcon
          action={<Button type="primary" onClick={handleBack}>Quay lại</Button>}
        />
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: 20 }}
        items={[
          { href: "/", title: <><HomeOutlined /> Trang chủ</> },
          { href: "/customers", title: <><UserOutlined /> Khách hàng</> },
          { title: <><EyeOutlined /> Chi tiết</> },
        ]}
      />

      {/* Header */}
      <CustomerHeader customer={customer} onBack={handleBack} />

      {/* Main Content */}
      <Row gutter={[24, 24]}>
        {/* Left Column - Customer Details */}
        <Col xs={24} lg={18}>
          <CustomerDetailsCard customer={customer} />
          <CollectionTable
            data={dataCollection}
            pagination={pagination}
            onChange={handleTableChange}
          />
          <AssetListTable assets={customer.assets} />
        </Col>

        {/* Right Column - Actions & Stats */}
        <Col xs={24} lg={6}>
          <ActionPanel
            customer={customer}
            updating={updating}
            onEdit={handleEdit}
            onToggleStatus={handleToggleStatus}
            onDelete={handleDelete}
          />
          <StatisticsCard customer={customer} />
        </Col>
      </Row>

      <CustomerFormDrawer
        open={editOpen}
        record={customer}
        onClose={() => setEditOpen(false)}
        onSaved={() => fetchCustomerInfo(id)}
      />
    </div>
  );
}